import { Component, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { Router } from '@angular/router';
import { AsamcoUserService } from '@asamco/base';
import { bind, pathJoin, isNullOrWhitespace, showErrorSwal } from '@asamco/utils';
import { DxScrollViewComponent, DxTagBoxModule, DxTooltipModule, DxScrollViewModule } from 'devextreme-angular';
import { BehaviorSubject, combineLatest, Observable, of, Subject } from 'rxjs';
import { catchError, debounceTime, map, startWith, switchMap, takeUntil, tap } from 'rxjs/operators';
import Swal from 'sweetalert2';
import { ApplicationTypeAL } from '../enums/ApplicationTypeAL.enum';
import { Application } from '../interfaces/application.interface';
import { MarketPlaceItem } from '../interfaces/market-place-item.interface';
import { SelectedCompanyService } from '../selected-company/selected-company.service';
import { ApplicationService } from '../services/application.service';
import { LinkService } from '../services/link.service';
import { MarketPlaceItemService } from '../services/market-place-item.service';
import { AdminMarketplaceManagementComponent } from './admin-marketplace-management/admin-marketplace-management.component';
import { TileInformationComponent } from './tile-information/tile-information.component';
import { ArraySortPipe } from '../pipes/sort.pipe';
import { DxTemplateModule } from 'devextreme-angular/core';
import { NgIf, NgFor, NgStyle, AsyncPipe } from '@angular/common';

@Component({
    selector: 'al-marketplace',
    templateUrl: './marketplace.component.html',
    styleUrls: ['./marketplace.component.scss', '../styles/tile-style.scss'],
    standalone: true,
    imports: [
        NgIf,
        DxTagBoxModule,
        DxTemplateModule,
        DxTooltipModule,
        NgFor,
        TileInformationComponent,
        DxScrollViewModule,
        NgStyle,
        AdminMarketplaceManagementComponent,
        AsyncPipe,
        ArraySortPipe,
    ],
})
export class MarketplaceComponent implements OnInit, OnDestroy {
	public imgUrl = '';

	@ViewChild('adminMenu')
	adminMenu: AdminMarketplaceManagementComponent;

	@ViewChild('tileInformation')
	tileInformation: TileInformationComponent;

	readonly defaultSkipAdd = 100;
	readonly searchTimeout = 1000;
	readonly maxFilterItems = 2;

	@ViewChild(DxScrollViewComponent, { static: false })
	scrollView: DxScrollViewComponent;
	pullDown: boolean = true;
	tagLimit: boolean = false;

	applications$: Observable<Application[]>;
	quicksearch$: Observable<Application[]>;

	items$: Observable<MarketPlaceItem[]>;
	items: MarketPlaceItem[];
	selectedLinkInformation: MarketPlaceItem;

	selectedApps: number[];
	skip: number;
	top: number;

	private selectedApps$ = new BehaviorSubject<number[]>([]);
	private reload$ = new Subject<void>();
	private destroy$ = new Subject<void>();

	public canManageApplicationLinkAdministrationSubject: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);

	constructor(
		private marketPlaceItemService: MarketPlaceItemService,
		private applicationService: ApplicationService,
		private router: Router,
		private linkService: LinkService,
		private selectedCompanyService: SelectedCompanyService,
		private asamcoUserService: AsamcoUserService
	) {
		this.skip = 0;
		this.top = this.defaultSkipAdd;
		this.selectedApps = [];
		this.imgUrl = pathJoin([window['deployUrl'], '/assets/img/']);
	}

	ngOnInit(): void {
		this.asamcoUserService.hasAssignedPermission('AL3_ADMINISTRATION').then(result => this.canManageApplicationLinkAdministrationSubject.next(result));
		this.applications$ = this.applicationService.get().pipe(
			map(data => data.filter(app => app.showOnMarketplace !== false)),
			map(data => {
				data.sort((a, b) => {
					return a.displayName.toLowerCase() < b.displayName.toLowerCase()  ? -1 : 1;
				});
				return data;
			})
		);
		this.quicksearch$ = this.applications$.pipe();
		this.loadMarketPlaceItems();
	}

	ngOnDestroy(): void {
		this.destroy$.next();
		this.destroy$.complete();
	}

	setHeaderColor(item: any) {

		let primaryHeaderItem: string = item.toAppHeaderImageRef;
		if(item.tileHeaderIsFromApp)
		{
			primaryHeaderItem = item.fromAppHeaderImageRef;
		}

		if (primaryHeaderItem == 'connectwiseheader.png') {
			return 'black';
		} else if (primaryHeaderItem == 'autotaskheader.png') {
			return '#026ccf';
		} else if (primaryHeaderItem == 'adnheader.png') {
			return '#B5B5B3';
		} else if (primaryHeaderItem == 'ingrammicroheader.png') {
			return '#2F75BB';
		} else if (primaryHeaderItem == 'alsoheader.png') {
			return '#6c757d';
		} else if (primaryHeaderItem == 'techdataheader.png') {
			return '#202A45';
		} else if (primaryHeaderItem == 'autotaskheader.png') {
			return '#026ccf';
		} else if(primaryHeaderItem == 'bmsheader.png') {
			return '#193E64'
		} else if (primaryHeaderItem == 'techdataionheader.png') {
			return '#FFFFFF';
		} else if (primaryHeaderItem == 'infiterraheader.png') {
			return '#100843';
		} else if (primaryHeaderItem == 'xeroheader.png') {
			return '#1aadd2';
		} else if (primaryHeaderItem == 'halopsaheader.png') {
			return '#f9384b';
		} else if (primaryHeaderItem == 'appriverheader.png') {
			return '#FFFFFF';
		} else if (primaryHeaderItem == 'arrowsphereheader.png') {
			return '#FFFFFF';
		} else if (primaryHeaderItem == 'syncroheader.png') {
			return '#FFFFFF';
		} else if (primaryHeaderItem == 'copacoheader.png') {
			return '#FFFFFF';
		} else if (primaryHeaderItem == 'swiss21abaninjaheader.png') {
			return '#FFFFFF';
		}
		return 'black';
	}

	/** Adds an app id to the selected App id array */
	public addToFilter(id: number): void {
		if (this.selectedApps.some(app => app === id)) {
			this.selectedApps = this.selectedApps.filter(app => app !== id);
		} else {
			this.selectedApps.push(id);
		}

		this.selectedApps$.next(this.selectedApps);
	}

	getProperImageRef(item: MarketPlaceItem) {
		if(item.tileHeaderIsFromApp)
		{
			return '/application-logos/' + item.fromAppHeaderImageRef;
		}
		else
		{
			return '/application-logos/' + item.toAppHeaderImageRef;
		}
	}

	getIntegrationTitleName(item: MarketPlaceItem): string
	{
		if(item.tileHeaderIsFromApp)
		{
			return item.toDName;
		}
		return item.fromDName;
	}

	filterAccounting(applications: Application[]): Application[] {
		return applications.filter(application => application.applicationType == ApplicationTypeAL.Accounting);
	}

	filterCSP(applications: Application[]): Application[] {
		return applications.filter(application => application.applicationType == ApplicationTypeAL.CSP);
	}

	filterPSA(applications: Application[]): Application[] {
		return applications.filter(application => application.applicationType == ApplicationTypeAL.PSA);
	}

	filterOther(applications: Application[]): Application[] {
		return applications.filter(application => application.applicationType == ApplicationTypeAL.Other);
	}

	/** Filters the items  */
	public filterChanged(e): void {
		if (e.value.length > this.maxFilterItems) {
			this.selectedApps = e.value.slice(0, this.maxFilterItems);
			this.tagLimit = true;
		} else {
			this.selectedApps = e.value;
		}
		this.selectedApps$.next(this.selectedApps);
	}

	public openTileInformation(e, item: any) {
		console.log(item);
		this.selectedLinkInformation = item;
		this.tileInformation.showModal();
	}

	/** Adds new application by navigating to the link edit page */
	public addApplication(e, id: number) {
		this.linkService
			.createLink(id)
			.pipe(
				catchError(err => {
					showErrorSwal(err);
					return of(0);
				})
			)
			.subscribe(result => {
				if (result > 0) {
					this.router.navigate(['application-link/link/' + result]);
				}
			});
	}

	public onPullDown($event) {
		setTimeout(this.enablePullDown, this.searchTimeout);
		this.reload$.next();
	}

	/** Method to re-enable pulldown of scrollview */
	@bind
	public enablePullDown() {
		this.pullDown = true;
	}

	/** Adds @defaultSkipAdd amount of items to the marketplace scrollview */
	public addContent(e) {
		this.skip += this.defaultSkipAdd;
		this.top += this.defaultSkipAdd;
		const temp$: Observable<MarketPlaceItem[]> = this.marketPlaceItemService.getDataSource(this.skip, this.top, this.selectedApps);
		temp$.pipe(takeUntil(this.destroy$)).subscribe(items => {
			items.forEach(item => this.items.push(item));
			e.component.release(items.length !== this.defaultSkipAdd);
			this.checkScrollUpdate();
			this.scrollView.instance.scrollBy(50);
		});
	}

	/** Loads a new set of marketplace items and removes the older items */
	public loadMarketPlaceItems() {
		// tslint:disable-next-line:deprecation
		this.items$ = combineLatest([this.selectedApps$, this.reload$.pipe(startWith(null))]).pipe(
			map(([selectedApps, reload]) => ({ selectedApps })),
			tap(() => {
				this.skip = 0;
				this.top = this.defaultSkipAdd;
			}),
			debounceTime(this.searchTimeout),
			switchMap(selectedApps => this.marketPlaceItemService.getDataSource(this.skip, this.top, this.selectedApps)),
			takeUntil(this.destroy$)
		);

		this.items$.subscribe(items => {
			this.items = items;
			this.scrollView.instance.release(items.length !== this.defaultSkipAdd);
			this.checkScrollUpdate();
			this.scrollView.instance.scrollTo(0);
		});
	}

	public manageLinkInformation() {
		this.adminMenu.setupModal();
	}

	/** Enables or disables scrollbar updating if there are no more items */
	public checkScrollUpdate() {
		if (this.items.length !== this.defaultSkipAdd) {
			this.scrollView.instance.beginUpdate();
		} else {
			this.scrollView.instance.endUpdate();
		}
	}
	/** checks if an app has been selected */
	public selected(id: number): boolean {
		return this.selectedApps.some(app => app === id);
	}

	public getUrl(subUrl: string): string {
		if (!isNullOrWhitespace(this.imgUrl) && !isNullOrWhitespace(subUrl)) {
			return pathJoin([this.imgUrl, subUrl]);
		}
		return '';
	}
}
